import React, { useEffect, useState } from "react";

// Shared "create/edit client" form used by InvoicesPage (Clients tab), SettingsPage
// (Clients tab) and NewJobPage (client picker quick-add). Centralizes the 8 backend
// fields (see services/backend/routers/jobs.py Client model + _validate_client_fields)
// and the client-side validation that mirrors the backend's checks, so all three entry
// points behave identically instead of drifting.

export interface ClientFormValues {
  name: string;
  contact_email: string;
  contact_person: string;
  address: string;
  tax_id: string;
  tax_label: string;
  tax_scheme: string;
  branch_number: string;
}

export const BLANK_CLIENT_FORM: ClientFormValues = {
  name: "", contact_email: "", contact_person: "", address: "",
  tax_id: "", tax_label: "", tax_scheme: "", branch_number: "",
};

export interface TaxSchemeOption {
  value: string;
  label: string;
}

// Default option set for the client's "default tax scheme" field. Pages that already
// maintain a richer TAX_SCHEMES structure (for invoice tax/WHT computation) can pass
// their own derived `taxSchemeOptions` to keep those values byte-identical to before;
// this default only applies where no such list previously existed.
export const CLIENT_TAX_SCHEME_OPTIONS: TaxSchemeOption[] = [
  { value: "", label: "Custom (no preset)" },
  { value: "thailand", label: "🇹🇭 Thailand" },
  { value: "india", label: "🇮🇳 India" },
  { value: "singapore", label: "🇸🇬 Singapore" },
  { value: "uk", label: "🇬🇧 United Kingdom" },
  { value: "uae", label: "🇦🇪 UAE" },
  { value: "australia", label: "🇦🇺 Australia" },
  { value: "zero_rated", label: "Zero-rated / Export" },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export interface ExistingClientRef {
  id?: number | string | null;
  name: string;
}

/** Mirrors backend `_validate_client_fields` (services/backend/routers/jobs.py):
 * required name (trimmed), valid email format, case-insensitive duplicate name
 * against clients already loaded on the page. Returns an error string, or null if valid. */
export function validateClientForm(
  values: ClientFormValues,
  existingClients: ExistingClientRef[] = [],
  excludeId?: number | string | null,
): string | null {
  const name = values.name.trim();
  if (!name) return "Company name is required.";

  // A client with no way to reach them saved silently (bug-42) — email is the one
  // contact detail actually needed for billing/communication, so it's the field
  // that's mandatory; Contact Person stays optional.
  const email = values.contact_email.trim();
  if (!email) return "Contact email is required.";
  if (!EMAIL_RE.test(email)) return "Please enter a valid email address.";

  const dupe = existingClients.some(c =>
    (excludeId == null || String(c.id) !== String(excludeId)) &&
    (c.name || "").trim().toLowerCase() === name.toLowerCase()
  );
  if (dupe) return `A client named "${name}" already exists.`;

  return null;
}

/** Builds the multipart FormData the backend endpoints (POST/PUT /api/jobs/clients) expect. */
export function clientValuesToFormData(values: ClientFormValues): FormData {
  const fd = new FormData();
  fd.append("name", values.name.trim());
  if (values.contact_email) fd.append("contact_email", values.contact_email);
  if (values.contact_person) fd.append("contact_person", values.contact_person);
  if (values.address) fd.append("address", values.address);
  if (values.tax_id) fd.append("tax_id", values.tax_id);
  if (values.tax_label) fd.append("tax_label", values.tax_label);
  if (values.tax_scheme) fd.append("tax_scheme", values.tax_scheme);
  if (values.branch_number) fd.append("branch_number", values.branch_number);
  return fd;
}

export interface ClientFormStyleOverrides {
  label?: React.CSSProperties;
  input?: React.CSSProperties;
  submitButton?: React.CSSProperties;
  cancelButton?: React.CSSProperties;
  error?: React.CSSProperties;
}

export interface ClientFormProps {
  value: ClientFormValues;
  onChange: (values: ClientFormValues) => void;
  /** Called only after the shared validation passes. */
  onSubmit: (values: ClientFormValues) => void;
  onCancel?: () => void;
  /** Already-loaded clients, used for the case-insensitive duplicate-name check. */
  existingClients?: ExistingClientRef[];
  /** When editing, pass the client's own id so it's excluded from the duplicate check. */
  excludeId?: number | string | null;
  isEditing?: boolean;
  submitting?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  /** "full" = all 8 fields always visible (Invoices/Settings manager tabs).
   *  "compact" = name/person/email up front, rest behind a "More fields" disclosure
   *  (Jobs page quick-add) — same field coverage, less UI weight by default. */
  mode?: "full" | "compact";
  taxSchemeOptions?: TaxSchemeOption[];
  /** Externally-supplied error (e.g. from a failed API call) shown alongside
   *  the form's own validation error, using the same inline slot. */
  error?: string | null;
  autoFocus?: boolean;
  styles?: ClientFormStyleOverrides;
}

const defaultLabelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};
const defaultInputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
const defaultSubmitButtonStyle: React.CSSProperties = {
  background: "#1e3a8a", color: "white", border: "none", padding: "9px 18px",
  borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13,
};
const defaultCancelButtonStyle: React.CSSProperties = {
  background: "none", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const defaultErrorStyle: React.CSSProperties = {
  color: "#dc2626", fontSize: 12, marginTop: 8,
};

export default function ClientForm({
  value, onChange, onSubmit, onCancel,
  existingClients = [], excludeId = null, isEditing = false,
  submitting = false, submitLabel, cancelLabel = "Cancel",
  mode = "full", taxSchemeOptions, error = null, autoFocus = false,
  styles = {},
}: ClientFormProps) {
  const [localError, setLocalError] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(mode === "full");

  // Clear the client-side validation error as soon as the user edits the form again.
  useEffect(() => { setLocalError(null); }, [value]);

  const labelStyle = { ...defaultLabelStyle, ...styles.label };
  const inputStyle = { ...defaultInputStyle, ...styles.input };
  const submitButtonStyle = { ...defaultSubmitButtonStyle, ...styles.submitButton };
  const cancelButtonStyle = { ...defaultCancelButtonStyle, ...styles.cancelButton };
  const errorStyle = { ...defaultErrorStyle, ...styles.error };

  const set = (field: keyof ClientFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      onChange({ ...value, [field]: e.target.value });

  const handleSubmit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    const validationError = validateClientForm(value, existingClients, excludeId);
    if (validationError) {
      setLocalError(validationError);
      return;
    }
    setLocalError(null);
    onSubmit(value);
  };

  // Submit on Enter from a single-line input, mirroring native <form> behavior.
  // Not wired to a real <form> element because this component is nested inside
  // a page-level <form> in NewJobPage — nested <form>s silently swallow the
  // inner submit event in React (neither onSubmit fires), so submission here
  // is handled entirely via button onClick + this key handler instead.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Scoped to text/email <input> only — SELECT and BUTTON are excluded so
    // Enter on "Cancel" / "+ More fields" / the Save button itself (which
    // browsers activate via a synthesized click on Enter) doesn't also
    // trigger this handler and double-submit.
    if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const displayError = localError || error;
  const showExtraFields = mode === "full" || showMore;
  const schemeOptions = taxSchemeOptions ?? CLIENT_TAX_SCHEME_OPTIONS;

  const fieldWrap: React.CSSProperties | undefined = mode === "compact" ? { marginBottom: 10 } : undefined;

  return (
    <div onKeyDown={handleKeyDown}>
      <div style={mode === "full" ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 } : undefined}>
        <div style={fieldWrap}>
          <label style={labelStyle}>Company Name *</label>
          <input autoFocus={autoFocus} value={value.name} onChange={set("name")} style={inputStyle} placeholder="Acme Corp" />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Contact Person</label>
          <input value={value.contact_person} onChange={set("contact_person")} style={inputStyle} placeholder="John Smith" />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Contact Email *</label>
          <input type="email" value={value.contact_email} onChange={set("contact_email")} style={inputStyle} placeholder="billing@acme.com" />
        </div>

        {showExtraFields && (
          <>
            <div style={fieldWrap}>
              <label style={labelStyle}>Tax Label</label>
              <input value={value.tax_label} onChange={set("tax_label")} style={inputStyle} placeholder="e.g. GST No., VAT No., TIN" />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Tax ID / Number</label>
              <input value={value.tax_id} onChange={set("tax_id")} style={inputStyle} placeholder="GST1234567" />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Default Tax Scheme</label>
              <select value={value.tax_scheme} onChange={set("tax_scheme")} style={inputStyle}>
                {schemeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Branch Number</label>
              <input value={value.branch_number} onChange={set("branch_number")} style={inputStyle} placeholder="00000 for Head Office" />
            </div>
            <div style={mode === "full" ? { gridColumn: "1 / -1" } : fieldWrap}>
              <label style={labelStyle}>Address</label>
              <textarea value={value.address} onChange={set("address")} style={{ ...inputStyle, height: 70, resize: "vertical" }}
                placeholder="123 Main St, City, State, Country" />
            </div>
          </>
        )}
      </div>

      {mode === "compact" && !showMore && (
        <button type="button" onClick={() => setShowMore(true)}
          style={{ background: "none", border: "none", color: "#1e3a8a", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: 0, marginBottom: 10 }}>
          + More fields (address, tax, branch)
        </button>
      )}

      {displayError && <div style={errorStyle}>{displayError}</div>}

      <div style={{ display: "flex", gap: 8, marginTop: mode === "full" ? 0 : 4 }}>
        <button type="button" onClick={() => handleSubmit()} disabled={submitting} style={{ ...submitButtonStyle, opacity: submitting ? 0.7 : 1 }}>
          {submitting ? "Saving…" : submitLabel || (isEditing ? "Save Changes" : "Add Client")}
        </button>
        {onCancel && <button type="button" onClick={onCancel} style={cancelButtonStyle}>{cancelLabel}</button>}
      </div>
    </div>
  );
}
